package gr.tech.pdfgenerator.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY
import gr.tech.pdfgenerator.util.format
import gr.tech.pdfgenerator.util.formatCurrency
import gr.tech.pdfgenerator.util.formatNumber
import gr.tech.pdfgenerator.infrastructure.calculations.recalculateProposalCalculations
import gr.tech.pdfgenerator.util.roundCents
import gr.tech.pdfgenerator.util.roundToOneDecimalPlace
import gr.tech.pdfgenerator.util.roundToTwoDecimalPlace
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.roundToLong

@Composable
fun ProposalCalculationsView() {

    val inputState by GeneratorData.propInputState
    var calculationState by GeneratorData.propCalculationsState
    val derivationsState by GeneratorData.derivationsState

    LaunchedEffect(inputState, derivationsState) {
        calculationState = recalculateProposalCalculations(inputState, derivationsState)
    }

    /*
        PART 2: LINKED VALUES
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).toAttrs()) {
        Text("Page 2: Linked Values")
    }
    Div(Modifier.width(600.px).margin(bottom = 50.px).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).border(2.px, LineStyle.Solid, Colors.Black).toAttrs()) {
        Div(Modifier.margin(bottom = 10.px).backgroundColor(Colors.DarkGreen).color(Colors.White).toAttrs()) {
            Row(Modifier.gap(10.percent) , horizontalArrangement = Arrangement.Start) {
                summaryBox("Net Project Cost", formatCurrency(calculationState.projectCost))
                summaryBox("Annual Savings", formatCurrency(calculationState.totalAnnualSavings))
                summaryBox("Annualized ROI",  if (calculationState.annualizedROI > 0) "${(calculationState.annualizedROI * 100).roundToLong()}%" else "0%")
            }
            Row(Modifier.gap(20.percent), horizontalArrangement = Arrangement.Start) {
                summaryBox("Simple Payback", "${calculationState.simplePayback.roundToOneDecimalPlace()}")
                summaryBox("Total Savings Over 10 Years", formatCurrency(calculationState.cummCashFlowList.last()))
            }
        }

        Div(Modifier.margin(topBottom = 10.px).toAttrs()) {
            summaryLine("Total Return on Investment (ROI)", if (calculationState.totalROI > 0) "${(calculationState.totalROI * 100).roundToTwoDecimalPlace()}%" else "0%")
            summaryLine("Annualized ROI (10 years)", if (calculationState.annualizedROI > 0) "${(calculationState.annualizedROI * 100).roundToLong()}%" else "0%")
            summaryLine("Simple Payback", "${calculationState.simplePayback.roundToOneDecimalPlace()} Years")
        }

        Div(Modifier.margin(topBottom = 10.px).toAttrs()) {
            summaryLine("Replacement Fixtures/Bulbs", formatCurrency(calculationState.replaceFixBulbs))
            summaryLine("Project Cost", formatCurrency(calculationState.projectCost))

            val salesTaxCalc: Double = (calculationState.projectCost * 0.075)
            summaryLine("Sales Tax", formatCurrency(calculationState.salesTax))
            summaryLine("Total Project Cost", formatCurrency(calculationState.totalProjectCost))
            summaryLine("Rebates", if (calculationState.rebates > 0) "-${formatCurrency(calculationState.rebates)}" else "$0") //appending negative to it, as it is being subtracted.
            summaryLine("Net Project Cost", formatCurrency(calculationState.netProjectCost))
        }

        Div(Modifier.margin(topBottom = 10.px).toAttrs()) {
            summaryLine("Annual Energy Savings (Lighting)", formatCurrency(calculationState.energySavingsLighting))
            summaryLine("Annual Energy Savings (HVAC)", formatCurrency(calculationState.energySavingsHVAC))
            summaryLine("Annual Maintenance Savings", formatCurrency(calculationState.maintenanceSavings))
            summaryLine("Total Annual Savings", formatCurrency(calculationState.totalAnnualSavings))
            summaryLine("Total Savings Over 10 years", formatCurrency(calculationState.cummCashFlowList.last()))
        }

        Div(Modifier.margin(topBottom = 10.px).toAttrs()) {
            summaryLine("Existing Energy Usage", "${formatNumber(inputState.baselineKWH.toString())} kWh")
            summaryLine("Proposed Energy Usage", "${formatNumber(inputState.proposedKWH.toString())} kWh")
            summaryLine("Energy Savings", "${formatNumber(derivationsState.annualKWHSaved.toString())} kWh")

            val energyPercent = (derivationsState.annualKWHSaved.toDouble() / inputState.baselineKWH.toDouble()).format(2).toDouble() //I hate this. Too many toDouble(), but for now it works?
            summaryLine("Energy Reduction", if (energyPercent > 0) "${(energyPercent * 100)}%" else "0%")
        }
    }

    /*
        PART 3: CASH FLOWS
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 3: Cash Flows")
    }
    Div(Modifier
        .margin(bottom = 50.px)
        .fontFamily(FONT_FAMILY)
        .fontSize(1.cssRem)
        .toAttrs()
    ) {
        Table(Modifier.width(1200.px).toAttrs{
            style {
                property("border-collapse", "collapse")
            }
        }) {
            Tr(blueBackStyle.toAttrs()) {
                Td() //empty top-left
                repeat(10) { columnIndex ->
                    Td(cellStyle.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                        Text("${columnIndex + 1}")
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier()).toAttrs()) {
                    Text("Project Cost")
                }
                Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                    Text(formatCurrency(calculationState.cashflowProjectCost.roundCents()))
                }
                repeat(9) {
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text("$0")
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier()).toAttrs()) {
                    Text("Energy Savings")
                }
                calculationState.energySavingList.forEachIndexed { _, amount ->
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text(formatCurrency(amount.roundCents()))
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier()).toAttrs()) {
                    Text("HVAC Savings")
                }
                calculationState.hvacSavingsList.forEachIndexed { _, amount ->
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text(formatCurrency(amount.roundCents()))
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier()).toAttrs()) {
                    Text("Maintenance Savings")
                }
                repeat(10) {
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text(formatCurrency(calculationState.maintenanceSavings))
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier().margin(top = 20.px)).toAttrs()) {
                    Text("Net Cash Flow")
                }
                calculationState.netCashFlowList.forEachIndexed { _, amount ->
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text(formatCurrency(amount.roundCents()))
                    }
                }
            }
            Tr {
                Td(blueBackStyle.toModifier().then(labelCellStyle.toModifier()).toAttrs()) {
                    Text("Cumulative Cash Flow")
                }
                calculationState.cummCashFlowList.forEachIndexed { _, amount ->
                    Td(cellStyle.toModifier().then(lightBlueBackStyle.toModifier()).toAttrs()) {
                        Text(formatCurrency(amount.roundCents()))
                    }
                }
            }
            Tr {
                Td(Modifier.fontWeight(FontWeight.Bold).toAttrs()) {
                    Text("IRR input")
                }
            }
            Tr(Modifier.border(2.px, LineStyle.Solid, Colors.Black).toAttrs()) {
                calculationState.irrInput.forEachIndexed { _, input ->
                    Td(cellStyle.toAttrs()) {
                        Text(formatCurrency(input.roundCents()))
                    }
                }
                Td(cellStyle.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                    Text(if (calculationState.annualizedROI > 0) "${(calculationState.annualizedROI * 100).roundToLong()}%" else "0%")
                }
            }
            Tr {
                Td(Modifier.fontWeight(FontWeight.Bold).toAttrs()) {
                    Text("IRR input 2")
                }
            }
            Tr(Modifier.border(2.px, LineStyle.Solid, Colors.Black).toAttrs()) {
                calculationState.irrInput2.forEachIndexed { _, input ->
                    Td(cellStyle.toAttrs()) {
                        Text(formatCurrency(input.roundCents()))
                    }
                }
                Td(cellStyle.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                    Text(if (calculationState.returnNoFinance > 0) "${(calculationState.returnNoFinance * 100).roundToLong()}%" else "0%")
                }
            }
            Tr {
                Td(Modifier.fontWeight(FontWeight.Bold).toAttrs()) {
                    Text("IRR input 3")
                }
            }
            Tr(Modifier.border(2.px, LineStyle.Solid, Colors.Black).toAttrs()) {
                calculationState.irrInput3.forEachIndexed { _, input ->
                    Td(cellStyle.toAttrs()) {
                        Text(formatCurrency(input.roundCents()))
                    }
                }
                Td(cellStyle.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                    Text(if (calculationState.returnFinanced > 0) "${(calculationState.returnFinanced * 100).roundToLong()}%" else "0%")
                }
            }
        }


        Div(Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).margin(topBottom = 50.px).toAttrs()) {
            Div(Modifier.toAttrs {
                id("cashflow-graph")
            }) {
                LaunchedEffect(calculationState.energySavingList.toMutableList()) {
                    updateCashflowGraph(calculationState.energySavingList.toMutableList())
                }
            }

            Div(Modifier.toAttrs {
                id("cummulflow-graph")
            }) {
                LaunchedEffect(calculationState.cummCashFlowList.toMutableList()) {
                    updateCummulCashFlowGraph(calculationState.cummCashFlowList.toMutableList())
                }
            }
        }
    }

    /*
        PART 4: COST WAITING
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 4: Cost of Waiting")
    }
    Div(Modifier
        .margin(bottom = 50.px)
        .fontFamily(FONT_FAMILY)
        .fontSize(1.cssRem)
        .toAttrs()
    ) {
        Table {
            Tr {
                repeat(5) { index ->
                    Td(cellStyle.toModifier().then(blueBackStyle.toModifier()).toAttrs()) {
                        Text("Year ${ index + 1 }")
                    }
                }
            }
            Tr {
                calculationState.costInYearsList.forEachIndexed { _, yearlyCost ->
                    Td(cellStyle.toAttrs()) {
                        Text(formatCurrency(yearlyCost.roundCents()))
                    }
                }
            }
        }

        Div(Modifier.toAttrs {
            id("costWaiting-graph")
        }) {
            LaunchedEffect(calculationState.costInYearsList) {
                updateCostWaitingGraph(calculationState.costInYearsList.toMutableList())
            }
        }
    }

    /*
        PART 5: SAVINGS ANALYSIS
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 5: Savings Analysis")
    }
    Div(Modifier
        .fontFamily(FONT_FAMILY)
        .fontSize(1.cssRem)
        .margin(bottom = 50.px)
        .fillMaxWidth()
        .toAttrs()
    ) {
        Div(Modifier.width(500.px).toAttrs()) {
            H3(Modifier.border(2.px, LineStyle.Solid, Colors.DarkOliveGreen).toAttrs()) {
                Text("Annual Savings")
            }
            P(DarkGreenBackStyle.toModifier().fontSize(24.px).toAttrs()) {
                Text(formatCurrency(calculationState.annualSavings))
            }
            Table(Modifier.width(400.px).toAttrs()) {
                Tr {
                    Td {
                        Text("Cost Savings")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text("${(calculationState.baselineCostSavings * 100).roundToLong()}%")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text("${(calculationState.ledCostSavings * 100).roundToLong()}%")
                    }
                }
                Tr {
                    Td {}
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text("Baseline")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text("LED")
                    }
                }
                Tr {
                    Td {
                        Text("Energy Costs")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.baselineEnergyCosts))
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.ledEnergyCosts))
                    }
                }
                Tr {
                    Td {
                        Text("Maintenance")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.baselineMaintenance))
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.ledMaintenance))
                    }
                }
                Tr {
                    Td {
                        Text("HVAC")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.baselineHVAC))
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.ledHVAC))
                    }
                }
                Tr {
                    Td {
                        Text("Total")
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.baselineTotal))
                    }
                    Td(Modifier.textAlign(TextAlign.Center).toAttrs()) {
                        Text(formatCurrency(calculationState.ledTotal))
                    }
                }
            }
        }

        Div(attrs = Modifier.margin(topBottom = 50.px).width(700.px).display(DisplayStyle.Flex).flexDirection(
            FlexDirection.Row).alignItems(AlignItems.Center).toAttrs()) {
            Div(Modifier.toAttrs {
                id("savingsAnalysis-graph")
            }) {
                LaunchedEffect((calculationState.ledCostSavings * 100).roundToLong(), (calculationState.baselineCostSavings * 100).roundToLong()) {
                    updateSavingsAnalysisGraph((calculationState.ledCostSavings * 100), (calculationState.baselineCostSavings * 100))
                }
            }

            Div(Modifier.margin(topBottom = 50.px).toAttrs {
                id("savingsBar-graph")
            }) {
                LaunchedEffect(calculationState.baselineEnergyCosts, calculationState.ledEnergyCosts, calculationState.baselineMaintenance, calculationState.baselineHVAC, calculationState.ledHVAC, calculationState.baselineTotal, calculationState.ledTotal) {
                    updateSavingsBarGraph(calculationState.baselineEnergyCosts, calculationState.ledEnergyCosts, calculationState.baselineMaintenance, calculationState.baselineHVAC, calculationState.ledHVAC, calculationState.baselineTotal, calculationState.ledTotal)
                }
            }
        }
    }

    /*
        PART 6: ENVIRONMENTAL IMPACTS
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 6: Environmental Impact")
    }
    Div(Modifier
        .fontFamily(FONT_FAMILY)
        .fontSize(1.cssRem)
        .margin(bottom = 50.px)
        .width(600.px).toAttrs()
    ) {
        Table(Modifier.width(600.px).margin(leftRight = autoLength).toAttrs()) {
            Tr {
                Td {
                    Text("Total Energy Savings")
                }
                Td {
                    Text("CO2 Reduced")
                }
                Td {
                    Text("Cars Removed")
                }
                Td {
                    Text("Trees Planted")
                }
            }
            Tr {
                Td(cellStyle.toModifier().toAttrs()) {
                    P(DarkGreenBackStyle.toModifier().fontSize(24.px).toAttrs()) {
                        Text(calculationState.totalEnergySavings.roundToLong().toString())
                    }
                }
                Td(cellStyle.toModifier().toAttrs()) {
                    P(DarkGreenBackStyle.toModifier().fontSize(24.px).toAttrs()) {
                        Text(calculationState.co2Reduced.roundToLong().toString())
                    }
                }
                Td(cellStyle.toModifier().toAttrs()) {
                    P(DarkGreenBackStyle.toModifier().fontSize(24.px).toAttrs()) {
                        Text(calculationState.carsRemoved.toString())
                    }
                }
                Td(cellStyle.toModifier().toAttrs()) {
                    P(DarkGreenBackStyle.toModifier().fontSize(24.px).toAttrs()) {
                        Text(calculationState.treesPlanted.toString())
                    }
                }
            }
        }

        LongInputLabel(
            label = "US Yearly total kWh",
            id = "US Yearly total kWh",
            initialValue = calculationState.usYearlyTotalkWh.toString()
        ) { numb ->
            calculationState.usYearlyTotalkWh = numb
        }

        LongInputLabel(
            label = "US Yearly total CO2",
            id = "US Yearly total CO2",
            initialValue = calculationState.usYearlyTotalco2.toString()
        ) { numb ->
            calculationState.usYearlyTotalco2 = numb
        }

        if (calculationState.usYearlyTotalkWh > 0.0) {
            calculationState.tonsCo2kWh = (calculationState.usYearlyTotalco2 / calculationState.usYearlyTotalkWh.toDouble())
        }
        P(Modifier.margin(right = 200.px).fontWeight(FontWeight.Bold).toAttrs()) {
            Text("Tons CO2/kWh")
        }
        Label {
            Text(calculationState.tonsCo2kWh.toString())
        }

        DoubleInputLabel(
            label = "Vehicle CO2/year",
            id = "Vehicle CO2/year",
            initialValue = calculationState.vehicleCo2Year
        ) { numb ->
            calculationState.vehicleCo2Year = numb
        }

        DoubleInputLabel(
            label = "Tree CO2/year",
            id = "Tree CO2/year",
            initialValue = calculationState.treeCo2Year
        ) { numb ->
            calculationState.treeCo2Year = numb
        }

    }

    /*
        PART 7: FINANCING PAGE
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 7: Financing")
    }
    Div(Modifier
        .fontFamily(FONT_FAMILY)
        .fontSize(1.cssRem)
        .margin(bottom = 50.px)
        .toAttrs()
    ) {

        Div(Modifier.width(500.px).margin(leftRight = autoLength).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).alignItems(AlignItems.Center).toAttrs()) {
            summaryLine("Financed Amount: ", formatCurrency(calculationState.financedAmount))
        }
        Row(Modifier.gap(50.px) , horizontalArrangement = Arrangement.Center) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                H3() {
                    Text("Up Front Cost")
                }
                Row(Modifier.gap(30.px), horizontalArrangement = Arrangement.Center) {
                    Column(Modifier, horizontalAlignment = Alignment.CenterHorizontally) {
                        P() {
                            Text("No Financing")
                        }
                        Span() {
                            Text(formatCurrency(calculationState.upfrontNoFinance))
                        }
                    }
                    Column(Modifier, horizontalAlignment = Alignment.CenterHorizontally) {
                        P() {
                            Text("Financed Rebate")
                        }
                        Span() {
                            Text(formatCurrency(calculationState.upfrontFinanced))
                        }
                    }
                }
            }
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                H3() {
                    Text("Investment Return")
                }
                Row(Modifier.gap(30.px), horizontalArrangement = Arrangement.Center) {
                    Column(Modifier, horizontalAlignment = Alignment.CenterHorizontally) {
                        P() {
                            Text("No Financing")
                        }
                        Span() {
                            Text(if (calculationState.returnNoFinance > 0) "${(calculationState.returnNoFinance * 100).roundToLong()}%" else "0%")
                        }
                    }
                    Column(Modifier, horizontalAlignment = Alignment.CenterHorizontally) {
                        P() {
                            Text("Financed Rebate")
                        }
                        Span() {
                            Text(if (calculationState.returnFinanced > 0) "${(calculationState.returnFinanced * 100).roundToLong()}%" else "0%")
                        }
                    }
                }
            }
            //TODO create graphs for financing.
            //TODO Waiting for Jesse on updated Finance calculations.
        }
    }

    /*
        PART 8: LEASING PAGE
     */
    H1(headerTitleStyle.toModifier().then(bottomDivider.toModifier()).fontSize(32.px).toAttrs()) {
        Text("Page 8: Leasing")
    }
    Div(Modifier.margin(bottom = 50.px).toAttrs()) {
        Div(Modifier.width(500.px).margin(leftRight = autoLength).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).alignItems(AlignItems.Center).toAttrs()) {
        }
    }
}

@Composable
fun summaryLine(label: String, value: Any, textmodifier: Modifier = Modifier) {
    Div(
        Modifier
            .fontFamily(FONT_FAMILY)
            .fillMaxWidth()
            .margin(leftRight = autoLength)
            .padding(leftRight = 10.px)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Row)
            .justifyContent(JustifyContent.SpaceBetween)
            .then(textmodifier)
            .toAttrs()
    ) {
        Span(Modifier.fontSize(14.px).toAttrs()) {
            Text(label)
        }
        Span(Modifier.fontSize(14.px).toAttrs()) {
            Text(value.toString())
        }
    }
}

@Composable
fun summaryBox(label: String, value: Any) {
    Div(
        Modifier
            .fontFamily(FONT_FAMILY)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .textAlign(TextAlign.Center)
            .justifyContent(JustifyContent.Center)
            .toAttrs()
    ) {
        Label {
            Text(label)
        }
        Span(Modifier.fontSize(1.5.cssRem).fontWeight(FontWeight.Bold).toAttrs()) {
            Text(value.toString())
        }
    }
}